import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {
	Avatar,
	Collapse,
	IconButton,
	List,
	ListItem,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
} from "@mui/material";
import React, {ForwardedRef, forwardRef, Fragment, useEffect, useState} from "react";
import {Location, LocationAncestry, LocationTree} from "../../api";
import {useLocalization} from "@smalox/shared";
import {LocationIcon} from "./LocationIcon";

export interface LocationTreeViewProps {
	locations: LocationTree[];
	selected?: LocationTree | Location | LocationAncestry;
	onSelect: (location: LocationTree) => void;
	loading?: boolean;
	disabledParentId?: number;
	expandLocationId?: number;
}

interface LocationTreeItemProps {
	location: LocationTree;
	collapsed: number[];
	selected?: LocationTree | Location;
	onSelect: (location: LocationTree) => void;
	onCollapseToggle: (locationId: number) => void;
	disabledParentId?: number;
	isParentDisabled?: boolean;
}

export const LocationTreeView = forwardRef(
	(props: LocationTreeViewProps, ref: ForwardedRef<HTMLUListElement>) => {
		const {locations, selected, onSelect, loading, disabledParentId, expandLocationId} = props;
		const [collapsed, setCollapsed] = useState<number[]>([]);

		useEffect(() => {
			if (expandLocationId && !collapsed.includes(expandLocationId)) {
				setCollapsed(prev => [...prev, expandLocationId]);
			}
		}, [expandLocationId]);

		const toggleCollapse = (locationId: number) => {
			if (collapsed.includes(locationId)) {
				setCollapsed(prev => prev.filter(id => id !== locationId));
			}
			else {
				setCollapsed(prev => [...prev, locationId]);
			}
		};

		return (
			<List dense sx={{opacity: loading ? 0.5 : 1}} ref={ref}>
				{locations?.map(location => (
					<LocationTreeItem
						key={location.id}
						location={location}
						selected={selected}
						onCollapseToggle={toggleCollapse}
						onSelect={onSelect}
						collapsed={collapsed}
						disabledParentId={disabledParentId}
						isParentDisabled={location.id === disabledParentId}
					/>
				))}
			</List>
		);
	},
);

function LocationTreeItem({
	location,
	collapsed,
	selected,
	onSelect,
	onCollapseToggle,
	disabledParentId,
	isParentDisabled,
}: LocationTreeItemProps) {
	const {t} = useLocalization();
	const hasChildren = location.children.length > 0;
	const isCollapsed = collapsed.includes(location.id);

	const isDisabled = isParentDisabled || location.id === disabledParentId;

	const handleCollapseToggle = (event: React.MouseEvent) => {
		event.stopPropagation();
		onCollapseToggle(location.id);
	};

	return (
		<Fragment>
			<ListItem
				secondaryAction={
					<IconButton onClick={handleCollapseToggle}>
						{hasChildren
							&& (isCollapsed
								? <ExpandLess />
								: <ExpandMore />)}
					</IconButton>
				}
				disablePadding
			>
				<ListItemButton
					onClick={() => onSelect(location)}
					selected={selected == location}
					disabled={isDisabled}
				>
					<ListItemAvatar>
						<Avatar>
							<LocationIcon type={location.type} />
						</Avatar>
					</ListItemAvatar>
					<ListItemText
						primary={location.name}
						secondary={location.type && t(`locations.types.${location.type}`)}
					/>
				</ListItemButton>
			</ListItem>
			{hasChildren && (
				<Collapse in={isCollapsed}>
					<List dense sx={{pl: 3}}>
						{location.children.map(childLocation => (
							<LocationTreeItem
								key={childLocation.id}
								location={childLocation}
								selected={selected}
								onCollapseToggle={onCollapseToggle}
								onSelect={onSelect}
								collapsed={collapsed}
								disabledParentId={disabledParentId}
								isParentDisabled={isDisabled}
							/>
						))}
					</List>
				</Collapse>
			)}
		</Fragment>
	);
}
